import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Divider,
  Grid,
  Stack,
  Chip,
  IconButton,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  DragIndicator,
  Reorder
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
  colorOptions: [
    { value: "#1a5f7a", label: "Ocean Blue" },
    { value: "#b22222", label: "Firebrick" },
    { value: "#2d3436", label: "Charcoal" },
    { value: "#6a89cc", label: "Livid" },
  ]
};

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const defaultData = {
  firstName: "Samira",
  lastName: "Hadid",
  currentPosition: "Senior HR Manager",
  phone: "+123-456-7890",
  email: "hello@reallygreatsite.com",
  city: "New York, NY",
  profile: "Results-oriented Human Resources Professional with over 8 years of experience in streamlining HR processes and fostering positive workplace cultures. Proven track record in talent acquisition, employee retention strategies, and organizational development.",
};

const defaultLeftSectionOrder = [
  'contact', 'education', 'skills', 'languages', 'certifications'
];

const defaultRightSectionOrder = [
  'profile', 'workExperience', 'volunteering', 'accomplishments', 'additionalInfo', 'interests', 'references'
];

const Resume38 = ({
  color = "#1a5f7a",
  font = "'Inter', sans-serif",
  fontSize = "13px",
  headingSize = "14px",
  fontStyle = "normal",
  sectionSpacing = 15,
  paragraphSpacing = 20,
  lineSpacing = 18,
  topBottomMargin = 20,
  sideMargins = 20,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
  linkedIn = '',
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  
  // Responsive width percentages
  const leftWidth = "35%";
  const rightWidth = "65%";
  
  const data = { 
    ...defaultData, 
    ...formData,
    linkedIn: linkedIn || formData.linkedIn || '',
  };

  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [customSections, setCustomSections] = useState({});
  
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const [sidebarPages, setSidebarPages] = useState([]);
  const leftContentRef = useRef(null);
  const rightContentRef = useRef(null);

  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
  };

  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    ...additionalStyles
  });

  const getSectionHeaderStyle = () => ({
    fontSize: headingSize,
    fontWeight: "bold",
    marginBottom: `${sectionSpacing}px`,
    color: color,
    ...globalFontStyle
  });

  const getContentSpacingStyle = () => ({
    marginBottom: `${sectionSpacing}px`,
    ...globalFontStyle
  });

  const getParagraphSpacingStyle = () => ({
    marginBottom: `${paragraphSpacing}px`,
    ...globalFontStyle
  });

  const getListItemSpacingStyle = () => ({
    marginBottom: `${paragraphSpacing / 2}px`,
    ...globalFontStyle
  });

  const SectionHeader = ({ title }) => (
    <Box style={{ marginBottom: `${sectionSpacing}px`, marginTop: "8px" }}>
      <Typography
        component="div"
        style={{
          fontWeight: 800,
          fontSize: headingSize,
          color: color,
          textTransform: "uppercase",
          letterSpacing: "1.5px",
          fontFamily: font,
          fontStyle: fontStyle,
          lineHeight: `${lineSpacing}px`,
          wordBreak: "break-word",
        }}
      >
        {title}
      </Typography>
      <Box style={{ 
        width: "40px", 
        height: "3px", 
        backgroundColor: color, 
        marginTop: "4px",
        borderWidth: `${lineWeight}px`
      }} />
    </Box>
  );

  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) setEducationEntries(JSON.parse(storedEntries));

    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) setSavedSkills(JSON.parse(storedSkills));

    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);

    const storedAdditionalInfo = JSON.parse(localStorage.getItem("additionalInfo")) || [];
    setSavedAdditionalInfo(storedAdditionalInfo);

    const storedLanguages = JSON.parse(localStorage.getItem("languagesList")) || [];
    setSavedLanguages(storedLanguages);

    const storedAccomplishments = JSON.parse(localStorage.getItem("accomplishmentsList")) || [];
    setSavedAccomplishments(storedAccomplishments);

    const storedCertifications = JSON.parse(localStorage.getItem("certificationsList")) || [];
    setSavedCertifications(storedCertifications);

    const storedReferences = JSON.parse(localStorage.getItem("referencesList")) || [];
    setSavedReferences(storedReferences);

    const storedSoftwareSkills = JSON.parse(localStorage.getItem("softwareSkills")) || [];
    setSoftwareSkills(storedSoftwareSkills);

    const storedVolunteering = JSON.parse(localStorage.getItem("volunteering")) || [];
    setSavedVolunteering(storedVolunteering);

    const storedInterests = JSON.parse(localStorage.getItem("interests")) || [];
    setSavedInterests(storedInterests);

    const userId = localStorage.getItem("userId");
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      currentCustomSections.forEach(sectionName => {
        const userCustomKey = `custom_${userId}_${sectionName}`;
        try {
          const customSectionData = localStorage.getItem(userCustomKey);
          if (customSectionData) {
            const parsedData = JSON.parse(customSectionData);
            const items = parsedData.items || [];
            const validItems = items.filter(item => item && item.trim() !== '');
            if (validItems.length > 0) sectionsData[sectionName] = validItems;
          }
        } catch (error) {
          console.error(`Error loading custom section ${sectionName}:`, error);
        }
      });
    }

    setCustomSections(sectionsData);

    const savedLeftSectionOrder = localStorage.getItem("resume38LeftSectionOrder");
    const savedRightSectionOrder = localStorage.getItem("resume38RightSectionOrder");

    if (savedLeftSectionOrder && savedRightSectionOrder) {
      let leftOrder = JSON.parse(savedLeftSectionOrder);
      let rightOrder = JSON.parse(savedRightSectionOrder);
      if (!leftOrder.includes('education')) {
        const contactIndex = leftOrder.indexOf('contact');
        if (contactIndex !== -1) {
          leftOrder.splice(contactIndex + 1, 0, 'education');
        } else {
          leftOrder.unshift('education');
        }
      }
      rightOrder = rightOrder.filter(item => item !== 'education');
      setLeftSectionOrder(leftOrder);
      setRightSectionOrder(rightOrder);
    } else {
      const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      setLeftSectionOrder([...defaultLeftSectionOrder, ...currentCustomSectionIds]);
      setRightSectionOrder([...defaultRightSectionOrder]);
    }
  }, []);

  useEffect(() => {
    if (leftSectionOrder.length > 0) {
      localStorage.setItem("resume38LeftSectionOrder", JSON.stringify(leftSectionOrder));
    }
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0) {
      localStorage.setItem("resume38RightSectionOrder", JSON.stringify(rightSectionOrder));
    }
  }, [rightSectionOrder]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  const handleLeftDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'left', sectionId: leftSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleRightDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'right', sectionId: rightSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleLeftDrop = (e, targetIndex) => {
    e.preventDefault();
    const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = dragData.index;
    const sourceSide = dragData.side;
    const sectionId = dragData.sectionId;

    let newLeftSectionOrder = [...leftSectionOrder];
    let newRightSectionOrder = [...rightSectionOrder];
    
    if (sourceSide === 'left') newLeftSectionOrder.splice(sourceIndex, 1);
    else if (sourceSide === 'right') newRightSectionOrder.splice(sourceIndex, 1);
    
    newLeftSectionOrder.splice(targetIndex, 0, sectionId);
    
    setLeftSectionOrder(newLeftSectionOrder);
    setRightSectionOrder(newRightSectionOrder);
    localStorage.setItem("resume38LeftSectionOrder", JSON.stringify(newLeftSectionOrder));
    localStorage.setItem("resume38RightSectionOrder", JSON.stringify(newRightSectionOrder));
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = dragData.index;
    const sourceSide = dragData.side;
    const sectionId = dragData.sectionId;

    let newLeftSectionOrder = [...leftSectionOrder];
    let newRightSectionOrder = [...rightSectionOrder];
    
    if (sourceSide === 'left') newLeftSectionOrder.splice(sourceIndex, 1);
    else if (sourceSide === 'right') newRightSectionOrder.splice(sourceIndex, 1);
    
    newRightSectionOrder.splice(targetIndex, 0, sectionId);
    
    setLeftSectionOrder(newLeftSectionOrder);
    setRightSectionOrder(newRightSectionOrder);
    localStorage.setItem("resume38LeftSectionOrder", JSON.stringify(newLeftSectionOrder));
    localStorage.setItem("resume38RightSectionOrder", JSON.stringify(newRightSectionOrder));
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetLeftSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    localStorage.setItem("resume38LeftSectionOrder", JSON.stringify(defaultLeftSectionOrder));
  };

  const resetRightSectionOrder = () => {
    const customSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
    setRightSectionOrder([...defaultRightSectionOrder, ...customSectionIds]);
    localStorage.setItem("resume38RightSectionOrder", JSON.stringify([...defaultRightSectionOrder, ...customSectionIds]));
  };

  const resetAllSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    const customSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
    setRightSectionOrder([...defaultRightSectionOrder, ...customSectionIds]);
  };

  const handleArrangeSectionsClick = () => setArrangeDialogOpen(true);
  const handleArrangeDialogClose = () => setArrangeDialogOpen(false);

  const getOverflowStyle = () => ({
    wordBreak: "break-word",
    overflowWrap: "break-word",
    whiteSpace: "normal"
  });

  const renderSection = (sectionId, isSidebar = false) => {
    const className = isSidebar ? "sidebar-section" : "resume-section";
    const overflowStyles = getOverflowStyle();
    
    switch (sectionId) {
      case 'profile':
        return (savedSummary || data.profile) ? (
          <div key="profile" className={className} style={{ ...getContentSpacingStyle(), ...overflowStyles }}>
            <SectionHeader title="Profile Summary" />
            <Typography sx={getTextStyle({ 
              color: "#555", 
              lineHeight: `${lineSpacing}px`,
              marginBottom: `${paragraphSpacing}px`,
              ...overflowStyles
            })}>
              {savedSummary || data.profile}
            </Typography>
          </div>
        ) : null;

      case 'workExperience':
        return workExperiences && workExperiences.length > 0 ? (
          <div key="workExperience" className={className} style={{ ...getContentSpacingStyle(), ...overflowStyles }}>
            <SectionHeader title="Work Experience" />
            <Box sx={{ borderLeft: `2px solid #eee`, pl: 3, ml: 1, ...globalFontStyle, ...overflowStyles }}>
              {workExperiences.map((work, i) => (
                <Box key={i} sx={{ marginBottom: `${paragraphSpacing}px`, position: "relative", ...overflowStyles }}>
                  <Box sx={{ position: "absolute", left: "-31px", top: "5px", width: "12px", height: "12px", borderRadius: "50%", bgcolor: color }} />
                  <Typography sx={getTextStyle({ fontWeight: 700, ...overflowStyles })}>
                    {work.jobTitle} | {formatDate(work.startMonth, work.startYear)} - {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                  </Typography>
                  <Typography sx={getTextStyle({ color: color, fontWeight: 600, marginBottom: `${paragraphSpacing / 2}px`, ...overflowStyles })}>
                    {work.employer}
                  </Typography>
                  {work.description && !isSidebar && (
                    <Box sx={{ ...globalFontStyle, ...overflowStyles }} dangerouslySetInnerHTML={{ __html: work.description }} />
                  )}
                </Box>
              ))}
            </Box>
          </div>
        ) : null;

      case 'education':
        return educationEntries.length > 0 ? (
          <div key="education" className={className} style={{ ...getContentSpacingStyle(), ...overflowStyles }}>
            <SectionHeader title="Education" />
            {educationEntries.map((edu) => {
              const institutionName = edu.educationLevel === "school" ? edu.schoolName : edu.collegeName;
              const degreeName = edu.educationLevel === "school" ? edu.standard : edu.degreeName;
              const fieldOfStudy = edu.educationLevel === "school" ? edu.stream : edu.specialization;
              const percentage = edu.percentage;
              const cgpa = edu.cgpa;
              const grade = edu.grade;
              const gradMonth = edu.gradMonth;
              const gradYear = edu.gradYear;
              
              return (
                <Box key={edu.id} sx={{ marginBottom: `${paragraphSpacing}px`, ...globalFontStyle, ...overflowStyles }}>
                  <Typography sx={getTextStyle({ fontWeight: 700, ...overflowStyles })}>
                    {degreeName} {fieldOfStudy && `- ${fieldOfStudy}`}
                  </Typography>
                  <Typography sx={getTextStyle({ marginBottom: `${paragraphSpacing / 2}px`, ...overflowStyles })}>
                    {institutionName} | {formatDate(gradMonth, gradYear)}
                  </Typography>
                  <Typography sx={getTextStyle({ color: '#666', ...overflowStyles })}>
                    {percentage && `${percentage}%`}
                    {cgpa && ` | CGPA: ${cgpa}`}
                    {grade && ` | Grade: ${grade}`}
                  </Typography>
                </Box>
              );
            })}
          </div>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <div key="volunteering" className={className} style={{ ...getContentSpacingStyle(), ...overflowStyles }}>
            <SectionHeader title="Volunteering" />
            <Box sx={{ borderLeft: `2px solid #eee`, pl: 3, ml: 1, ...globalFontStyle, ...overflowStyles }}>
              {savedVolunteering.map((vol, index) => (
                <Box key={index} sx={{ marginBottom: `${paragraphSpacing}px`, position: "relative", ...overflowStyles }}>
                  <Box sx={{ position: "absolute", left: "-31px", top: "5px", width: "12px", height: "12px", borderRadius: "50%", bgcolor: color }} />
                  <Typography sx={getTextStyle({ fontWeight: 700, ...overflowStyles })}>{vol.subtopic}</Typography>
                  <Typography sx={getTextStyle({ color: color, fontWeight: 600, marginBottom: `${paragraphSpacing / 2}px`, ...overflowStyles })}>
                    {vol.fromDate} - {vol.toDate}
                  </Typography>
                  <Typography sx={getTextStyle({ color: "#666", ...overflowStyles })}>{vol.content}</Typography>
                </Box>
              ))}
            </Box>
          </div>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <div key="accomplishments" className={className} style={{ ...getContentSpacingStyle(), ...overflowStyles }}>
            <SectionHeader title="Accomplishments" />
            {savedAccomplishments.map((acc, index) => (
              <Typography key={index} sx={{ ...getListItemSpacingStyle(), ...overflowStyles }}>• {acc}</Typography>
            ))}
          </div>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <div key="additionalInfo" className={className} style={{ ...getContentSpacingStyle(), ...overflowStyles }}>
            <SectionHeader title="Additional Information" />
            {savedAdditionalInfo.map((info, index) => (
              <Typography key={index} sx={{ ...getListItemSpacingStyle(), ...overflowStyles }}>• {info}</Typography>
            ))}
          </div>
        ) : null;

      case 'contact':
        return (data.phone || data.email || data.city || data.linkedIn) ? (
          <div key="contact" className={className} style={{ ...getContentSpacingStyle(), ...overflowStyles }}>
            <SectionHeader title="Contact" />
            <Stack spacing={1.5} sx={{ ...globalFontStyle, ...overflowStyles }}>
              {data.phone && (
                <Stack direction="row" alignItems="center" gap={0.5} sx={overflowStyles}>
                  <PhoneIcon sx={{ fontSize: 16, color: color, flexShrink: 0 }} />
                  <Typography sx={{ ...getTextStyle(), ...overflowStyles }}>{data.phone}</Typography>
                </Stack>
              )}
              {data.email && (
                <Stack direction="row" alignItems="center" gap={0.5} sx={overflowStyles}>
                  <EmailIcon sx={{ fontSize: 16, color: color, flexShrink: 0 }} />
                  <Typography sx={{ ...getTextStyle(), ...overflowStyles }}>{data.email}</Typography>
                </Stack>
              )}
              {data.city && (
                <Stack direction="row" alignItems="center" gap={0.5} sx={overflowStyles}>
                  <LocationOnIcon sx={{ fontSize: 16, color: color, flexShrink: 0 }} />
                  <Typography sx={{ ...getTextStyle(), ...overflowStyles }}>{data.city}</Typography>
                </Stack>
              )}
              {data.linkedIn && (
                <Stack direction="row" alignItems="center" gap={0.5} sx={overflowStyles}>
                  <LinkedInIcon sx={{ fontSize: 16, color: color, flexShrink: 0 }} />
                  <Typography sx={{ ...getTextStyle(), ...overflowStyles }}>{data.linkedIn}</Typography>
                </Stack>
              )}
            </Stack>
          </div>
        ) : null;

      case 'skills':
        return (savedSkills.length > 0 || softwareSkills.length > 0) ? (
          <div key="skills" className={className} style={{ ...getContentSpacingStyle(), ...overflowStyles }}>
            <SectionHeader title="Top Skills" />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, ...globalFontStyle, ...overflowStyles }}>
              {savedSkills.map((skill, index) => (
                <Chip key={index} label={skill.name} size="small" sx={{ borderRadius: "4px", bgcolor: "#fff", border: `1px solid #ddd`, fontSize: "11px", fontWeight: 600 }} />
              ))}
              {softwareSkills.map((skill, index) => (
                <Chip key={`software-${index}`} label={skill.name} size="small" sx={{ borderRadius: "4px", bgcolor: "#fff", border: `1px solid #ddd`, fontSize: "11px", fontWeight: 600 }} />
              ))}
            </Box>
          </div>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <div key="languages" className={className} style={{ ...getContentSpacingStyle(), ...overflowStyles }}>
            <SectionHeader title="Languages" />
            <Stack spacing={1.5} sx={{ ...globalFontStyle, ...overflowStyles }}>
              {savedLanguages.map((lang, index) => (
                <Box key={index} sx={overflowStyles}>
                  <Typography sx={getTextStyle({ fontWeight: 700, ...overflowStyles })}>
                    {lang.name} {lang.level && `(${lang.level})`}
                  </Typography>
                  <Box sx={{ width: "100%", height: "4px", bgcolor: "#eee", mt: 0.5 }}>
                    <Box sx={{ width: lang.level === "Native" ? "95%" : lang.level === "Advanced" ? "85%" : lang.level === "Intermediate" ? "70%" : lang.level === "Beginner" ? "50%" : "70%", height: "100%", bgcolor: color }} />
                  </Box>
                </Box>
              ))}
            </Stack>
          </div>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <div key="certifications" className={className} style={{ ...getContentSpacingStyle(), ...overflowStyles }}>
            <SectionHeader title="Certifications" />
            {savedCertifications.map((cert, index) => (
              <Box key={index} sx={{ marginBottom: `${paragraphSpacing}px`, ...globalFontStyle, ...overflowStyles }}>
                <Typography sx={getTextStyle({ fontWeight: 700, ...overflowStyles })}>{cert.name}</Typography>
                <Typography sx={getTextStyle({ ...overflowStyles })}>{cert.provider} {cert.year && `(${cert.year})`}</Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <div key="interests" className={className} style={{ ...getContentSpacingStyle(), ...overflowStyles }}>
            <SectionHeader title="Interests" />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ...globalFontStyle, ...overflowStyles }}>
              {savedInterests.map((interest, index) => (
                <Chip key={index} label={interest} size="small" sx={{ borderRadius: "4px", bgcolor: "#fff", border: `1px solid #ddd`, fontSize: "11px", fontWeight: 600 }} />
              ))}
            </Box>
          </div>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <div key="references" className={className} style={{ ...getContentSpacingStyle(), ...overflowStyles }}>
            <SectionHeader title="References" />
            {savedReferences.map((ref, index) => (
              <Box key={index} sx={{ marginBottom: `${paragraphSpacing}px`, ...globalFontStyle, ...overflowStyles }}>
                <Typography sx={getTextStyle({ fontWeight: 600, ...overflowStyles })}>{ref}</Typography>
              </Box>
            ))}
          </div>
        ) : null;

      default:
        if (sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          const validItems = items.filter(item => item && item.trim() !== '');
          
          if (validItems.length > 0) {
            return (
              <div key={sectionId} className={className} style={{ ...getContentSpacingStyle(), ...overflowStyles }}>
                <SectionHeader title={customSectionName.replace(/_/g, ' ')} />
                {validItems.map((item, i) => (
                  <Typography key={i} sx={{ ...getListItemSpacingStyle(), ...overflowStyles }}>• {item}</Typography>
                ))}
              </div>
            );
          }
        }
        return null;
    }
  };

  const renderLeftContentSections = () => leftSectionOrder.map(sectionId => renderSection(sectionId, true)).filter(Boolean);
  const renderRightSidebarSections = () => rightSectionOrder.map(sectionId => renderSection(sectionId, false)).filter(Boolean);

  const getSectionTitle = (sectionId) => {
    const titles = {
      'contact': 'Contact', 'education': 'Education', 'skills': 'Skills', 'languages': 'Languages',
      'profile': 'Profile', 'workExperience': 'Work Experience', 'certifications': 'Certifications',
      'accomplishments': 'Accomplishments', 'volunteering': 'Volunteering', 'interests': 'Interests',
      'additionalInfo': 'Additional Information', 'references': 'References'
    };
    if (sectionId.startsWith('custom_')) return sectionId.replace('custom_', '').replace(/_/g, ' ').toUpperCase();
    return titles[sectionId] || sectionId;
  };

  const renderDragPanel = (side, sections) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', ...globalFontStyle }}>
        <DragIndicator sx={{ mr: 1 }} />
        {side === 'left' ? 'Left Sidebar' : 'Right Main Content'} ({sections.length} sections)
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
        {sections.map((sectionId, index) => (
          <Paper key={sectionId} draggable
            onDragStart={(e) => side === 'left' ? handleLeftDragStart(e, index) : handleRightDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index, side)} onDragLeave={handleDragLeave}
            onDrop={(e) => side === 'left' ? handleLeftDrop(e, index) : handleRightDrop(e, index)} onDragEnd={handleDragEnd}
            sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center',
              backgroundColor: dragOverIndex === index && dragOverSide === side ? '#f0f0f0' : 'white',
              cursor: 'grab', '&:active': { cursor: 'grabbing' }, transition: 'all 0.2s ease' }}>
            <DragIndicator color="action" />
            <Typography sx={{ ml: 1, flexGrow: 1, ...globalFontStyle }}>{getSectionTitle(sectionId)}</Typography>
            <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
          </Paper>
        ))}
      </Box>
      <Button onClick={side === 'left' ? resetLeftSectionOrder : resetRightSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>Reset {side === 'left' ? 'Left' : 'Right'} Order</Button>
    </Box>
  );

  const paginateLeftContent = useCallback(() => {
    if (!leftContentRef.current) return;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const headerHeight = 220;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;
    const leftContent = leftContentRef.current;
    const sections = Array.from(leftContent.querySelectorAll('.sidebar-section'));
    
    let newLeftPages = [], currentPageSections = [], currentHeight = 0;
    
    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;
      if (currentHeight + sectionHeight > availableHeight && currentPageSections.length > 0) {
        newLeftPages.push([...currentPageSections]);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
      if (index === sections.length - 1 && currentPageSections.length > 0) newLeftPages.push([...currentPageSections]);
    });
    
    setSidebarPages(newLeftPages.length ? newLeftPages : [[]]);
  }, [page.height, topBottomMargin, leftSectionOrder, sectionSpacing, paragraphSpacing]);

  const paginateRightContent = useCallback(() => {
    if (!rightContentRef.current) return;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const headerHeight = 220;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;
    const rightContent = rightContentRef.current;
    const sections = Array.from(rightContent.querySelectorAll('.resume-section'));
    
    let newPages = [], currentPageSections = [], currentHeight = 0;
    
    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;
      if (currentHeight + sectionHeight > availableHeight && currentPageSections.length > 0) {
        newPages.push([...currentPageSections]);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
      if (index === sections.length - 1 && currentPageSections.length > 0) newPages.push([...currentPageSections]);
    });
    
    setPages(newPages.length ? newPages : [[]]);
  }, [page.height, topBottomMargin, rightSectionOrder, sectionSpacing, paragraphSpacing]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      paginateLeftContent();
      paginateRightContent();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [paginateLeftContent, paginateRightContent, educationEntries, savedSkills, savedSummary, workExperiences,
      savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications, savedReferences, softwareSkills,
      savedVolunteering, savedInterests, customSections, leftSectionOrder, rightSectionOrder, sectionSpacing,
      paragraphSpacing, lineSpacing, fontSize, headingSize, font, color, lineWeight, topBottomMargin, formData]);

  const addFontToHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    
    const baseStyles = `font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word;`;
    const spanStyles = `font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px;`;
    
    // For divs - preserve existing font-size if present
    htmlContent = htmlContent.replace(/<div([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      if (style.includes('font-size:')) {
        const newStyle = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word; ${style}`;
        return `<div${before}style="${newStyle}"`;
      } else {
        const newStyle = `${baseStyles}${style}`;
        return `<div${before}style="${newStyle}"`;
      }
    });
    htmlContent = htmlContent.replace(/<div(?![^>]*style)([^>]*)>/g, `<div$1style="${baseStyles}">`);
    
    // For p tags - preserve existing font-size if present
    htmlContent = htmlContent.replace(/<p([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      if (style.includes('font-size:')) {
        const newStyle = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word; ${style}`;
        return `<p${before}style="${newStyle}"`;
      } else {
        const newStyle = `${baseStyles}${style}`;
        return `<p${before}style="${newStyle}"`;
      }
    });
    htmlContent = htmlContent.replace(/<p(?![^>]*style)([^>]*)>/g, `<p$1style="${baseStyles}">`);
    
    // For span tags - preserve existing font-size if present
    htmlContent = htmlContent.replace(/<span([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      if (style.includes('font-size:')) {
        const newStyle = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; ${style}`;
        return `<span${before}style="${newStyle}"`;
      } else {
        const newStyle = `${spanStyles}${style}`;
        return `<span${before}style="${newStyle}"`;
      }
    });
    htmlContent = htmlContent.replace(/<span(?![^>]*style)([^>]*)>/g, `<span$1style="${spanStyles}">`);
    
    return htmlContent;
  };

  const renderLeftContentForMeasurement = () => (
    <Box ref={leftContentRef} sx={{ visibility: 'hidden', position: 'absolute', top: -1000, width: leftWidth, ...globalFontStyle }}>
      {renderLeftContentSections()}
    </Box>
  );

  const renderRightContentForMeasurement = () => (
    <Box ref={rightContentRef} sx={{ visibility: 'hidden', position: 'absolute', top: -1000, width: rightWidth, ...globalFontStyle }}>
      {renderRightSidebarSections()}
    </Box>
  );

  const totalPages = Math.max(sidebarPages.length, pages.length, 1);

  return (
    <Box sx={{ width: "100%", position: "relative", m: 5, ...globalFontStyle }}>
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, '@media print': { display: 'none' } }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0}><ChevronLeft /></IconButton>
          <Typography>Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1}><ChevronRight /></IconButton>
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, ml: 2 }}>
              <Button onClick={handleArrangeSectionsClick} variant="contained" startIcon={<Reorder />}>Arrange Sections</Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" size="small">Reset All</Button>
            </Box>
          )}
        </Box>
      )}
      
      {renderLeftContentForMeasurement()}
      {renderRightContentForMeasurement()}
      
      <Box id="resume-container">
        {Array.from({ length: totalPages }).map((_, pageIndex) => (
          <Box key={pageIndex} className="resume-page" sx={{
            width: page.width, minHeight: page.height, mx: "auto", bgcolor: "#fff",
            boxShadow: pageIndex === currentPage ? 3 : 0, borderTop: `10px solid ${color}`,
            marginTop: `${topBottomMargin}px`, marginBottom: `${topBottomMargin}px`,
            paddingLeft: `${sideMargins}px`, paddingRight: `${sideMargins}px`,
            "@media print": { width: page.width, height: "auto", minHeight: page.height, boxShadow: "none", m: 0,
              borderTop: `10px solid ${color}`, marginTop: `${topBottomMargin}px`, marginBottom: `${topBottomMargin}px`,
              paddingLeft: `${sideMargins}px`, paddingRight: `${sideMargins}px` },
            display: exporting ? 'flex' : (pageIndex === currentPage ? 'flex' : 'none'),
            '@media print': { display: 'flex' }, flexDirection: "column"
          }}>
            {pageIndex === 0 && (
              <Box sx={{ p: 4, pb: 2, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", ...globalFontStyle }}>
                <Avatar src={photo || "https://via.placeholder.com/200"} sx={{ width: 120, height: 120, borderRadius: "12px", border: `2px solid #eee` }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h3" sx={{ fontWeight: 900, color: "#333", lineHeight: 1, fontFamily: font, fontSize: "2.5rem", wordBreak: 'break-word' }}>
                    {data.firstName} {data.lastName}
                  </Typography>
                  <Typography sx={{ color: color, fontWeight: 500, letterSpacing: 2, mt: 1, fontFamily: font, fontSize: "1.5rem", wordBreak: 'break-word' }}>
                    {data.currentPosition}
                  </Typography>
                  <Stack direction="row" spacing={3} mt={2} flexWrap="wrap" sx={{ wordBreak: 'break-word' }}>
                    {data.phone && <Stack direction="row" alignItems="center" gap={0.5}><PhoneIcon sx={{ fontSize: 16, color: color }} /><Typography>{data.phone}</Typography></Stack>}
                    {data.email && <Stack direction="row" alignItems="center" gap={0.5}><EmailIcon sx={{ fontSize: 16, color: color }} /><Typography>{data.email}</Typography></Stack>}
                    {data.city && <Stack direction="row" alignItems="center" gap={0.5}><LocationOnIcon sx={{ fontSize: 16, color: color }} /><Typography>{data.city}</Typography></Stack>}
                  </Stack>
                </Box>
              </Box>
            )}

            {pageIndex === 0 && <Divider sx={{ mx: 4, borderWidth: `${lineWeight}px` }} />}

            <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0 }}>
              <Box sx={{ width: leftWidth, p: 4, pt: 2, bgcolor: "#f9f9f9", overflow: 'hidden' }}>
                {sidebarPages[pageIndex] ? (
                  <div style={globalFontStyle} dangerouslySetInnerHTML={{ __html: addFontToHtmlContent(sidebarPages[pageIndex].join('')) }} />
                ) : <Typography>Loading content...</Typography>}
              </Box>

              <Box sx={{ width: rightWidth, p: 4, pt: 2, overflow: 'hidden' }}>
                {pages[pageIndex] ? (
                  <div style={globalFontStyle} dangerouslySetInnerHTML={{ __html: addFontToHtmlContent(pages[pageIndex].join('')) }} />
                ) : null}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      <Dialog open={arrangeDialogOpen} onClose={handleArrangeDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle><Reorder sx={{ mr: 1 }} /> Arrange Resume Sections</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>Drag and drop sections between columns. Any section can be placed on either side!</Typography>
          <Box sx={{ display: "flex", gap: 3, width: "100%" }}>
            <Box sx={{ flex: 1 }}>{renderDragPanel("left", leftSectionOrder)}</Box>
            <Box sx={{ flex: 1 }}>{renderDragPanel("right", rightSectionOrder)}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder}>Reset All</Button>
          <Button onClick={handleArrangeDialogClose} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume38;